/* eslint-disable no-console */
// Util compartido de desvinculación de pagos. Extrae la lógica core que vivía acoplada a
// VincularMovimientoModal (handleDesvincularPagoCompleto / handleDesvincularCaja) para que también la
// pueda invocar EstadoCuenta desde las filas de pago del ledger (ver pendiente 9.9).
//
// Patrón fire-and-pray del codebase: cada paso loguea su error pero no aborta el resto
// (preferimos completar la desvinculación aunque queden refs huérfanas que limpiar después).
// El client se recibe por parámetro (default al singleton) para facilitar testing.

import { client } from "../supabase";
import { escribirEventosSaldo } from "../vincularFacturasCuotas";

// Campos que devuelven un movimiento bancario a 'pendiente', limpiando caja/estado/match_*. Fuente única
// de verdad del reset: la reusan los handlers del modal (VincularMovimientoModal) para no duplicar el
// shape ni olvidar campos (10.1). B3 Fase B paso 2: ya NO se resetean las FK directas
// `factura_sii_emitida_id` / `factura_sii_recibida_id` — nadie las lee (Fase A) ni las escribe (Fase B),
// y el vínculo lo borra el DELETE del puente MBF. R6 (Fase C): las FK escalares `plan_pago_cuota_id` /
// `plan_cobro_cuota_id` fueron DROPEADAS (0 lectores; el vínculo cuota↔pago vive en el libro
// `aplicaciones_saldo.cuota_id` + el puente MBF) → ya no se resetean.
export const RESET_BANCARIO = {
  movimiento_caja_id: null,
  estado: "pendiente",
  procesado_por: null,
  procesado_at: null,
  match_factura_tipo: null,
  match_factura_id: null,
  match_entidad_tipo: null,
  match_entidad_id: null,
  match_entidad_nombre: null,
  match_confianza: null,
  match_razon: null,
};

// Revierte a 'pendiente' las cuotas de plan de PAGOS (facturas recibidas) y de COBROS (facturas
// emitidas) que quedaron atadas a este movimiento bancario. Simétrico al reverso de creditos_cuotas /
// plan_pagos_activos: filtra por movimiento_bancario_id, así solo toca las cuotas de ESTE movimiento —
// inofensivo para los demás pagos. N-tolerante e idempotente (0 filas = el movimiento no pagaba/cobraba
// ninguna cuota, caso normal). Devuelve el conteo revertido por lado. NO traga errores de PostgREST
// (10.170 / 10.177): loguea cada lado por separado sin abortar el resto de la desvinculación
// (patrón fire-and-pray del util). El vínculo cuota↔movimiento vive en dos columnas redundantes
// (plan_*_facturas.movimiento_bancario_id y movimientos_bancarios.plan_*_cuota_id): esta función
// limpia el lado de la cuota; RESET_BANCARIO limpia el lado del movimiento.
export const revertirCuotasFacturaPorMovimiento = async ({ movimientoBancarioId, supabaseClient = client }) => {
  if (!movimientoBancarioId) return { pagos: 0, cobros: 0 };
  let pagos = 0;
  let cobros = 0;

  // Lado PAGOS (facturas recibidas): estado→pendiente + se despega el movimiento y su fecha real.
  try {
    const { data, error } = await supabaseClient
      .from("plan_pagos_facturas")
      .update({ estado: "pendiente", movimiento_bancario_id: null, fecha_pago_real: null })
      .eq("movimiento_bancario_id", movimientoBancarioId)
      .select("id");
    if (error) throw error;
    pagos = (data || []).length;
  } catch (err) {
    console.error("revertirCuotasFacturaPorMovimiento: error revirtiendo plan_pagos_facturas:", err);
  }

  // Lado COBROS (facturas emitidas): espejo exacto — fecha_cobro_real en vez de fecha_pago_real.
  try {
    const { data, error } = await supabaseClient
      .from("plan_cobros_facturas")
      .update({ estado: "pendiente", movimiento_bancario_id: null, fecha_cobro_real: null })
      .eq("movimiento_bancario_id", movimientoBancarioId)
      .select("id");
    if (error) throw error;
    cobros = (data || []).length;
  } catch (err) {
    console.error("revertirCuotasFacturaPorMovimiento: error revirtiendo plan_cobros_facturas:", err);
  }

  return { pagos, cobros };
};

// Desvincula un pago con vinculación directa vía MBF (equivale a handleDesvincularPagoCompleto al borrar
// la última fila): borra MBF, resetea el banco, borra la caja y libera las facturas externas.
export const desvincularPagoMBF = async ({ movimientoBancarioId, cajaId, supabaseClient = client }) => {
  try {
    // 1. SELECT filas MBF del movimiento + 2. capturar antes del DELETE: externas (para liberarlas),
    //    TODOS los documentos (para borrar SOLO sus eventos del libro) y las emitidas (para limpiar su
    //    facturafechacobro). Se captura acá porque el DELETE de MBF de abajo los borra.
    let externasLiberadas = [];
    let docsDesvinculados = []; // [{ documento_tipo, documento_id }]
    let emitidasIds = [];
    try {
      const { data: filas, error } = await supabaseClient
        .from("movimiento_bancario_facturas")
        .select("factura_tipo, factura_id")
        .eq("movimiento_bancario_id", movimientoBancarioId);
      if (error) throw error;
      externasLiberadas = (filas || []).filter((f) => f.factura_tipo === "factura_externa").map((f) => f.factura_id);
      docsDesvinculados = (filas || []).map((f) => ({ documento_tipo: f.factura_tipo, documento_id: f.factura_id }));
      emitidasIds = (filas || []).filter((f) => f.factura_tipo === "sii_emitida").map((f) => f.factura_id);
    } catch (err) {
      console.error("desvincularPagoMBF: error leyendo filas MBF:", err);
    }

    // Fase 1.1c/1.2a/1.2b: si el movimiento es SII PURO, EXTERNAS/BOLETAS PURO o cualquier MEZCLA entre
    // sii_recibida/factura_externa/boleta_honorario (pago), delegar en la RPC transaccional (unwind
    // atómico + limpieza de libro/facturafechacobro/estado_vinculacion/facturas_externas/
    // vinculaciones_forzadas). Para pago, NO si es compra_activo. compra_activo/mezcla con OTRO tipo
    // → camino viejo de abajo. Devuelve el MISMO shape { ok, error } del util (no throw).
    const tiposMBF = new Set(docsDesvinculados.map((d) => d.documento_tipo));
    const TIPOS_PAGO_RPC = new Set(["sii_recibida", "factura_externa", "boleta_honorario"]);
    const soloEmitidas = tiposMBF.size === 1 && tiposMBF.has("sii_emitida");
    const pagoElegibleRpc = tiposMBF.size > 0 && [...tiposMBF].every((t) => TIPOS_PAGO_RPC.has(t));
    let compraActivo = false;
    if (pagoElegibleRpc) {
      try {
        const { data: ca } = await supabaseClient
          .from("movimientos_caja")
          .select("activo_id")
          .eq("movimiento_bancario_id", movimientoBancarioId)
          .not("activo_id", "is", null)
          .maybeSingle();
        compraActivo = !!ca?.activo_id;
      } catch {
        compraActivo = false;
      }
    }
    if (soloEmitidas || (pagoElegibleRpc && !compraActivo)) {
      const rpcName = soloEmitidas ? "desvincular_cobro_bancario" : "desvincular_pago_bancario";
      const { error } = await supabaseClient.rpc(rpcName, {
        p_movimiento_id: movimientoBancarioId,
        p_usuario_id: null,
      });
      if (error) return { ok: false, error };
      return { ok: true, externasLiberadas: [] };
    }

    // 3. DELETE todas las filas MBF del movimiento.
    try {
      const { error } = await supabaseClient
        .from("movimiento_bancario_facturas")
        .delete()
        .eq("movimiento_bancario_id", movimientoBancarioId);
      if (error) throw error;
    } catch (err) {
      console.error("desvincularPagoMBF: error borrando filas MBF:", err);
    }

    // 4. UPDATE banco a pendiente (FKs + match_* limpios).
    if (movimientoBancarioId) {
      try {
        const { error } = await supabaseClient
          .from("movimientos_bancarios")
          .update(RESET_BANCARIO)
          .eq("id", movimientoBancarioId);
        if (error) throw error;
      } catch (err) {
        console.error("desvincularPagoMBF: error reseteando movimiento bancario:", err);
      }
    }

    // 5. DELETE la caja.
    if (cajaId) {
      try {
        const { error } = await supabaseClient.from("movimientos_caja").delete().eq("id", cajaId);
        if (error) throw error;
      } catch (err) {
        console.error("desvincularPagoMBF: error borrando movimiento de caja:", err);
      }
    }

    // 6. Liberar facturas externas vinculadas (vuelven a pendiente / disponibles para revincular).
    if (externasLiberadas.length > 0) {
      try {
        const { error } = await supabaseClient
          .from("facturas_externas")
          .update({ estado: "pendiente", movimiento_bancario_id: null })
          .in("id", externasLiberadas);
        if (error) throw error;
      } catch (err) {
        console.error("desvincularPagoMBF: error liberando facturas externas:", err);
      }
    }

    // 7. Revertir cuotas de plan de pagos/cobros atadas a este movimiento (huérfano histórico:
    //    ningún path lo hacía). El propio helper loguea sin abortar.
    await revertirCuotasFacturaPorMovimiento({ movimientoBancarioId, supabaseClient });

    // 8. Borrar del libro `aplicaciones_saldo` SOLO los eventos de los documentos desvinculados de este
    //    movimiento — NO con `[]` a secas: un movimiento puede pagar/cobrar varias facturas y este util
    //    saca las de esta operación; se borra por (origen, documento) para no tocar eventos de otras.
    //    Cierra la asimetría vincular↔desvincular en el ledger del eje pago/cobro (10.190). Best-effort:
    //    el unwind principal ya corrió; si falla, loguea visible (no se traga — patrón del util).
    if (docsDesvinculados.length > 0) {
      const { ok, error } = await escribirEventosSaldo(
        supabaseClient,
        { origen_tipo: "movimiento_bancario", origen_id: movimientoBancarioId },
        [],
        { soloDocumentos: docsDesvinculados }
      );
      if (!ok) console.error("desvincularPagoMBF: error borrando eventos de aplicaciones_saldo:", error);
    }

    // 9. Limpiar el rastro stale de cobro en las emitidas desvinculadas: facturafechacobro → null (el
    //    vincular lo setea a la fecha del mov). estado_vinculacion NO se toca: refiere al vínculo con el
    //    CONCEPTO de venta (negocios_carteleria/sales), no al cobro; una emitida vinculada a su concepto
    //    y sin cobro es 'vinculada' y así entra a "Por cobrar".
    if (emitidasIds.length > 0) {
      try {
        const { error } = await supabaseClient
          .from("facturas_sii_emitidas")
          .update({ facturafechacobro: null })
          .in("id", emitidasIds);
        if (error) throw error;
      } catch (err) {
        console.error("desvincularPagoMBF: error limpiando facturafechacobro de emitidas:", err);
      }
    }

    return { ok: true, externasLiberadas };
  } catch (err) {
    return { ok: false, error: err };
  }
};

// Detecta si desvincular este pago dejaría huérfano un evento vigente de una proyección —
// Edificios (`pagos_capex`, por `movimiento_bancario_id`) o Cartelería (`costos_proyecto_vinculos`
// tipo='anticipo', por `movimiento_caja_id`, Fase 5a). Reemplaza el fallback roto de la regla 10.200
// (`plan_pagos_activos.estado`, columna retirada 2026-09-13 — 10.258; el error quedaba tragado por
// el catch, ver docs/decisiones/2026-10-04-proyeccion-factura-pago-paso0-vincular-a-factura.md §d).
// Caso real que lo motiva: bancario 12443 (pago_a_cuenta de $571.190, cuota 117 del activo 498) —
// borrar su caja sin este guard deja `pagos_capex.id=87` vigente sin ningún movimiento real detrás.
// NUNCA silencioso (10.177): cualquier error de consulta se loguea, pero no bloquea por error de
// RED — solo bloquea cuando efectivamente ENCUENTRA un evento vigente.
export const detectarBloqueoProyeccion = async ({ movimientoBancarioId, cajaId, supabaseClient = client }) => {
  if (movimientoBancarioId) {
    try {
      const { data, error } = await supabaseClient
        .from("pagos_capex")
        .select("id")
        .eq("movimiento_bancario_id", movimientoBancarioId)
        .is("reversado_at", null)
        .limit(1);
      if (error) throw error;
      if (data && data.length > 0) {
        return {
          bloqueado: true,
          mensaje:
            "Este pago cubre una proyección de Edificios (Proyectos/CapEx) todavía vigente. " +
            "Para desvincularlo, primero revertí esa cuota desde la pestaña Proyectos de este mismo modal.",
        };
      }
    } catch (err) {
      console.error("detectarBloqueoProyeccion: error consultando pagos_capex:", err);
    }
  }

  if (cajaId) {
    try {
      const { data, error } = await supabaseClient
        .from("costos_proyecto_vinculos")
        .select("id")
        .eq("movimiento_caja_id", cajaId)
        .eq("tipo", "anticipo")
        .is("reversado_at", null)
        .limit(1);
      if (error) throw error;
      if (data && data.length > 0) {
        return {
          bloqueado: true,
          mensaje:
            "Este pago está vinculado como anticipo de un costo de proyecto de Cartelería todavía " +
            "vigente. Para desvincularlo, primero desvinculá el anticipo desde la ventana Costos del proyecto.",
        };
      }
    } catch (err) {
      // best-effort: si la tabla/columna no existe (Fase 5a sin aplicar), sigue sin bloquear por esto.
      console.error("detectarBloqueoProyeccion: error consultando costos_proyecto_vinculos:", err);
    }
  }

  return { bloqueado: false, mensaje: null };
};

// Desvincula un pago que vive solo en caja (sin MBF) — equivale a handleDesvincularCaja:
// resetea el banco a pendiente (si lo hay) y borra la caja. No toca facturas_externas.
export const desvincularPagoCaja = async ({ movimientoBancarioId, cajaId, supabaseClient = client }) => {
  // Guard (Fase A, 2026-10-04): no borrar la caja si deja huérfano un evento vigente de pagos_capex
  // o de un anticipo de Cartelería — ver detectarBloqueoProyeccion arriba.
  const { bloqueado, mensaje } = await detectarBloqueoProyeccion({ movimientoBancarioId, cajaId, supabaseClient });
  if (bloqueado) {
    return { ok: false, error: new Error(mensaje), bloqueado: true, mensaje };
  }
  try {
    // 1. UPDATE banco a pendiente (solo si el pago tiene movimiento bancario asociado).
    if (movimientoBancarioId) {
      try {
        const { error } = await supabaseClient
          .from("movimientos_bancarios")
          .update(RESET_BANCARIO)
          .eq("id", movimientoBancarioId);
        if (error) throw error;
      } catch (err) {
        console.error("desvincularPagoCaja: error reseteando movimiento bancario:", err);
      }
    }

    // 2. DELETE la caja.
    if (cajaId) {
      try {
        const { error } = await supabaseClient.from("movimientos_caja").delete().eq("id", cajaId);
        if (error) throw error;
      } catch (err) {
        console.error("desvincularPagoCaja: error borrando movimiento de caja:", err);
      }
    }

    // 3. Revertir cuotas de plan de pagos/cobros atadas a este movimiento (mismo huérfano que MBF).
    await revertirCuotasFacturaPorMovimiento({ movimientoBancarioId, supabaseClient });

    return { ok: true };
  } catch (err) {
    return { ok: false, error: err };
  }
};
